import { SpeechBubble } from '@/components/alisa/SpeechBubble';
import { Checkbox } from '@/components/ui/checkbox';
import { ALISA_IMAGES } from '@/data/moduleData';
import { useT } from '@/i18n';
import { motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';
import { useMemo, useState } from 'react';

const shuffleArray = (array) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

export const ChecklistScene = ({ scene, onComplete, onContinue }) => {
  const t = useT();
  const [checkedItems, setCheckedItems] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  const shuffledItems = useMemo(() => shuffleArray(scene.items), [scene.id, scene.items]);

  const requiredItems = scene.items.filter(item => item.required);
  const checkedRequiredCount = requiredItems.filter(item => checkedItems[item.id]).length;
  const allRequiredChecked = checkedRequiredCount === requiredItems.length;

  const handleToggle = (itemId) => {
    if (isSubmitted) return;
    setCheckedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const handleSubmit = () => {
    setIsSubmitted(true);
    onComplete(scene.id, checkedRequiredCount, requiredItems.length);
    setTimeout(() => setShowFeedback(true), 400);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col flex-1"
    >
      {/* Scene image and Alisa Character Overlay */}
      <div className="relative w-full aspect-[16/9] overflow-hidden bg-muted">
        {scene.image && (
          <img
            src={scene.image}
            alt={scene.title}
            className="w-full h-full object-cover"
          />
        )}

        {/* Overlaid Alisa Character */}
        <motion.div
          key={showFeedback ? (allRequiredChecked ? 'approval' : 'serious') : 'neutral'}
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          className="absolute left-0 bottom-0 h-[100%] z-10 pointer-events-none"
        >
          <img
            src={showFeedback
              ? (allRequiredChecked ? ALISA_IMAGES.approval : ALISA_IMAGES.serious)
              : ALISA_IMAGES.neutral
            }
            alt={t('ui.alisa.name')}
            className="h-full w-auto object-contain object-bottom"
          />
        </motion.div>

        {/* Shadow Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent pointer-events-none" />
      </div>

      <div className="flex-1 px-4 py-4 flex flex-col gap-3">
        {/* Alisa's message (Instruction -> Feedback) */}
        <SpeechBubble
          text={showFeedback ? (allRequiredChecked ? scene.feedback_complete : scene.feedback_partial) : scene.alisa_text}
          emotion={showFeedback ? (allRequiredChecked ? 'approval' : 'serious') : scene.alisa_emotion}
          key={showFeedback ? 'feedback' : 'instruction'}
        />

        {/* Checklist items */}
        <div className="flex flex-col gap-2 mt-1">
          {shuffledItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + index * 0.08 }}
              onClick={() => handleToggle(item.id)}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all duration-200 ${isSubmitted && item.required && !checkedItems[item.id]
                ? 'border-destructive/50 bg-destructive/5'
                : checkedItems[item.id]
                  ? 'border-success/50 bg-success/5'
                  : 'border-border bg-card hover:border-primary/20'
                } ${isSubmitted ? 'cursor-default' : ''}`}
            >
              <Checkbox
                checked={!!checkedItems[item.id]}
                disabled={isSubmitted}
                className={`${checkedItems[item.id]
                  ? 'border-success bg-success text-success-foreground data-[state=checked]:bg-success data-[state=checked]:border-success'
                  : ''
                  }`}
              />
              <span className={`text-sm font-body flex-1 ${checkedItems[item.id] ? 'text-foreground' : 'text-muted-foreground'
                }`}>
                {item.text}
              </span>
              {isSubmitted && item.required && !checkedItems[item.id] && (
                <AlertTriangle size={16} className="text-destructive shrink-0" />
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Action button */}
      <div className="px-4 pb-4">
        {!isSubmitted ? (
          <motion.button
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            onClick={handleSubmit}
            className="w-full gradient-primary-btn text-primary-foreground font-heading font-bold text-base py-3.5 rounded-xl shadow-primary hover:shadow-primary-hover active:scale-[0.98] transition-transform duration-150 uppercase tracking-wide"
          >
            {t('ui.common.check')}
          </motion.button>
        ) : showFeedback ? (
          <motion.button
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={onContinue}
            className="w-full gradient-primary-btn text-primary-foreground font-heading font-bold text-base py-3.5 rounded-xl shadow-primary hover:shadow-primary-hover active:scale-[0.98] transition-transform duration-150 uppercase tracking-wide"
          >
            {t('ui.common.continue')}
          </motion.button>
        ) : null}
      </div>
    </motion.div>
  );
};
